import { z } from 'zod';

/** Viewer-relative scope label attached to a row when listed for the manager
 *  UI, so the client can tab by Personal / Firm / Starter without re-deriving
 *  ownership (see scopeForVisibility in src/services/firmPlaybooks/types.ts). */
export const PLAYBOOK_SCOPES = ['personal', 'org', 'starter'] as const;
export type PlaybookScope = (typeof PLAYBOOK_SCOPES)[number];

// A playbook's engine gate reuses the note engine vocabulary — the engine
// FAMILIES (optiland | zemax | codev; hosted and BYO Zemax share one); null
// means engine-agnostic (loads on every session). Mirrored by the
// firm_playbooks / firm_playbook_files CHECK constraints (migration 100).
export const PLAYBOOK_ENGINES = ['optiland', 'zemax', 'codev'] as const;
export type PlaybookEngine = (typeof PLAYBOOK_ENGINES)[number];

/** Coarse extractability of an uploaded firm file, decided at upload from
 *  content-type + extension + size (never by extracting): 'extracted' = text/PDF
 *  the readFirmFile tool can inline; 'binary' = images / .zmx / .zar never
 *  returned as text; 'too_large' = a PDF over MAX_PDF_BYTES (oversized plain
 *  text is still 'extracted' — it reads as a labeled truncated prefix);
 *  'empty' = zero bytes. */
export const EXTRACT_STATUSES = ['extracted', 'binary', 'too_large', 'empty'] as const;
export type ExtractStatus = (typeof EXTRACT_STATUSES)[number];

// Firm Playbooks ("optical company brain"). Distinct from conversation
// Visibility: 'org' is authoritative on organization_id (a firm's IP cannot
// sit on an email-domain heuristic), and 'everyone' is the Photonium-curated
// starter tier.
export const PLAYBOOK_VISIBILITIES = ['personal', 'org', 'everyone'] as const;
export type PlaybookVisibility = (typeof PLAYBOOK_VISIBILITIES)[number];

// 'playbook' = an on-match procedure; 'context' = an always-on standing
// convention; 'template' = a re-appliable parameterized config (a converged
// merit/tolerance set) materialized on a new system via applyTemplate. A
// template carries its payload in firm_playbooks.config (not markdown `body`).
export const PLAYBOOK_KINDS = ['playbook', 'context', 'template'] as const;
export type PlaybookKind = (typeof PLAYBOOK_KINDS)[number];

export const PLAYBOOK_STATUSES = ['draft', 'staging', 'published'] as const;
export type PlaybookStatus = (typeof PLAYBOOK_STATUSES)[number];

export const PLAYBOOK_SOURCES = ['agent', 'ui', 'imported'] as const;
export type PlaybookSource = (typeof PLAYBOOK_SOURCES)[number];

/** One entry in the "what the agent loaded" legibility surface (gap B). */
export const LoadedContextEntrySchema = z.object({
  name: z.string(),
  scope: z.enum(PLAYBOOK_SCOPES),
});
export type LoadedContextEntry = z.infer<typeof LoadedContextEntrySchema>;

/** A matchable playbook surfaced to the agent as a skill: its name + description
 *  are always visible to the model; the body loads only on semantic match. */
export const LoadedPlaybookEntrySchema = z.object({
  name: z.string(),
  description: z.string(),
  scope: z.enum(PLAYBOOK_SCOPES),
  engine: z.enum(PLAYBOOK_ENGINES).nullable(),
});
export type LoadedPlaybookEntry = z.infer<typeof LoadedPlaybookEntrySchema>;

/** One entry in the always-on firm-file manifest and the gap-B legibility
 *  surface. Metadata only — never any file bytes. `importable` flags a Zemax
 *  system file (.zmx) that offers "import as system" instead of a text read. */
export const LoadedFirmFileEntrySchema = z.object({
  name: z.string(),
  ref: z.string(),
  content_type: z.string(),
  description: z.string(),
  extract_status: z.enum(EXTRACT_STATUSES),
  scope: z.enum(PLAYBOOK_SCOPES),
  importable: z.boolean(),
});
export type LoadedFirmFileEntry = z.infer<typeof LoadedFirmFileEntrySchema>;

/** The firm knowledge steering an agent turn for a given user + engine,
 *  computed from the exact same runtime rows the agent loads (see
 *  summarizeLoadedContext) so this surface can never drift from reality.
 *  `context` is always-on; each playbook is available to match (we cannot know
 *  which bodies the model actually pulled — that is honest, not hidden). An
 *  always-on rule the firm considers binding is an ordinary `context` entry at
 *  org scope, not a separate tier. `files` is the always-on manifest of the
 *  referenceable file corpus (metadata only, gap J). */
export const LoadedFirmContextSchema = z.object({
  context: z.array(LoadedContextEntrySchema),
  playbooks: z.array(LoadedPlaybookEntrySchema),
  files: z.array(LoadedFirmFileEntrySchema),
  templates: z.array(LoadedPlaybookEntrySchema),
  // Rows the engine gate hides from this session — filled by the
  // /playbooks/loaded route (extra count queries), not by the pure summarizer.
  hidden_by_engine: z
    .object({ playbooks: z.number(), templates: z.number(), files: z.number() })
    .optional(),
});

export type LoadedFirmContext = z.infer<typeof LoadedFirmContextSchema>;
